import React, { useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Button, Checkbox, DeleteIcon, IconButton, Row, Select, Spinner, TextField, Tooltip, Typography, useConfirmation } from "@coolremote/shared";
import { useReadModbusRegister, useValidateModbusFormula } from "../../../../hooks/useModbusDevicesApi";
import { useGetSiteModbusFunctionalTemplates } from "../../../../hooks/useModbusFunctionalTemplatesApi";
import {
  IEnumEntry,
  IModbusDeviceRow,
  IModbusFunctionalRow,
  IModbusFunctionalTemplate,
  MODBUS_ENTITY_TYPE_OPTIONS,
  ModbusCustomEnum,
  ModbusEntityType,
  UI_TYPE_BINARY,
  UI_TYPE_ENUMERATION,
  UI_TYPE_REVERSE_BINARY,
  UI_TYPE_VALUE,
} from "../../types";
import {
  BIT_NUMBER_OPTIONS,
  DATA_TYPE_CODE_OPTIONS,
  ENG_UNIT_OPTIONS,
  ErrorPointCell,
  IRegisterColumn,
  REG_TYPE_NUMERIC_OPTIONS,
  isBitPackableRegisterType,
  RegisterTable,
  UI_TYPE_OPTIONS,
  UniversalCell,
} from "../../RegisterTable";
import EnumMapEditor from "../../EnumMapEditor";
import BinaryMapDialog from "../../BinaryMapDialog";
import PropertyMappingDialog from "../../PropertyMappingDialog";
import ImportFromLibraryDialog from "../ImportFromLibraryDialog";
import ControlUiLibraryDialog, { IControlUiSelection } from "../ControlUiLibraryDialog";
import UITemplateMapperDialog, { IUITemplate } from "../../UITemplateMapper";
import useTypes from "../../../../hooks/useTypes";
import styles from "./AddFunctionalUnitModal.module.css";
import registerTableStyles from "../../RegisterTable/RegisterTable.module.css";

const recordToEntries = (m?: Record<string, string>): IEnumEntry[] =>
  Object.entries(m || {}).map(([value, text]) => ({ value, text }));

/** Convert-formula (readFormula) validation states — see validateFormulaRow. */
const FORMULA_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  WARN: "warn",
  ERROR: "error",
} as const;
type FormulaStatus = (typeof FORMULA_STATUS)[keyof typeof FORMULA_STATUS];

const newRow = (): IModbusDeviceRow => ({
  name: "",
  readOnly: false,
  uiType: UI_TYPE_VALUE,
  units: null,
  minValue: null,
  maxValue: null,
  step: null,
  registerType: 2,
  registerAddress: null,
  dataType: "s16",
  bitNumber: null,
  multiplier: 1,
});

interface IProps {
  open: boolean;
  initialName?: string;
  initialRows?: IModbusDeviceRow[];
  /** "" means not mapped to any universal entity. */
  initialEntityType?: ModbusEntityType | "";
  initialUiTemplate?: IUITemplate;
  saving?: boolean;
  deviceId?: string;
  slaveId?: number | string;
  iface?: string;
  host?: string;
  port?: number | string;
  /** The device's configured read timeout — the register Test button uses it. */
  timeoutMs?: number | string;
  unitIndex?: number;
  siteId?: string;
  /** Control UI names already used by other units on this same (unsaved) device draft. */
  siblingUiNames?: string[];
  /** When false, Bit # and Convert controls are disabled (firmware < v1.11 / itcVersion < 17). */
  supportsBitConvert?: boolean;
  onClose: () => void;
  onSave: (
    unitName: string,
    rows: IModbusDeviceRow[],
    entityTemplate: boolean,
    uiTemplate: IUITemplate | undefined,
    entityType: ModbusEntityType | undefined,
  ) => void;
}

const AddFunctionalUnitModal: React.FC<IProps> = ({
  open,
  initialName = "",
  initialRows,
  initialEntityType = "",
  initialUiTemplate,
  saving,
  deviceId,
  slaveId,
  iface,
  host,
  port,
  timeoutMs,
  unitIndex,
  siteId,
  siblingUiNames,
  supportsBitConvert = true,
  onClose,
  onSave,
}) => {
  const confirm = useConfirmation();
  const [importLibraryOpen, setImportLibraryOpen] = useState(false);
  const [controlUiLibraryOpen, setControlUiLibraryOpen] = useState(false);
  const [uiTemplateOpen, setUiTemplateOpen] = useState(false);
  const [uiTemplate, setUiTemplate] = useState<IUITemplate | undefined>(initialUiTemplate);
  const [unitName, setUnitName] = useState(initialName);
  const [rows, setRows] = useState<IModbusDeviceRow[]>(initialRows ?? [newRow()]);
  const [entityType, setEntityType] = useState<ModbusEntityType | "">(initialEntityType);
  const [submitted, setSubmitted] = useState(false);
  const [enumTarget, setEnumTarget] = useState<number | null>(null);
  const [binaryTarget, setBinaryTarget] = useState<number | null>(null);
  const [propertyTarget, setPropertyTarget] = useState<number | null>(null);
  const [verifyState, setVerifyState] = useState<Record<number, { status: "idle" | "loading" | "success" | "failed"; value?: any }>>({});
  const [formulaState, setFormulaState] = useState<Record<number, { status: FormulaStatus; message?: string }>>({});
  const validatingFormulaIndexRef = useRef<number | null>(null);
  const lastTrimmedFormulaRef = useRef<string>("");
  const [isValidatingFormulas, setIsValidatingFormulas] = useState(false);
  const validatedFormulaTextRef = useRef<Record<number, string>>({});

  const skipFormulaBlurRef = useRef(false);
  const handleDialogMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest(".cancel-button, .base-dialog-close-btn")) {
      skipFormulaBlurRef.current = true;
      setTimeout(() => { skipFormulaBlurRef.current = false; }, 0);
    }
  };

  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Reveal the right side of the params table (dropdown + remove button) when a
  // universal point is enabled, so the layout doesn't feel like it's hiding it.
  const scrollTableRight = () =>
    requestAnimationFrame(() => {
      const el = tableScrollRef.current;
      if (el) el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    });

  const { mutate: readRegister } = useReadModbusRegister();

  // Existing Control UI names on this site, so the mapper dialog can block
  // saving a duplicate (the library has no other place to enforce this).
  // Sibling units on this same (possibly unsaved) device draft aren't in the
  // library yet, so their names are merged in separately by the caller.
  const { data: siteFunctionalTemplates } = useGetSiteModbusFunctionalTemplates(siteId, open) as unknown as {
    data: IModbusFunctionalTemplate[] | null;
  };
  const existingControlUiNames = useMemo(
    () => [
      ...(siteFunctionalTemplates ?? [])
        .map((tpl) => (tpl.defaultUiTemplate as IUITemplate | undefined)?.name)
        .filter((name): name is string => !!name),
      ...(siblingUiNames ?? []),
    ],
    [siteFunctionalTemplates, siblingUiNames],
  );

  const types: any = useTypes();
  const propsForType: Record<string, any> =
    (entityType && types?.universalEntityPropertiesByEntityType?.[entityType]) ?? {};
  const universalOptions = Object.entries(propsForType).map(([key, def]: [string, any]) => ({
    value: key,
    label: def.text || key,
  }));

  useEffect(() => {
    if (open) {
      const nextRows: IModbusDeviceRow[] = initialRows ? JSON.parse(JSON.stringify(initialRows)) : [newRow()];
      setUnitName(initialName);
      setRows(nextRows);
      setEntityType(initialEntityType);
      setUiTemplate(initialUiTemplate);
      setSubmitted(false);
      setVerifyState({});
      setFormulaState({});
      validatedFormulaTextRef.current = Object.fromEntries(
        nextRows.map((row, index) => [index, (row.readFormula ?? "").toString().trim()]),
      );
      validatingFormulaIndexRef.current = null;
      lastTrimmedFormulaRef.current = "";
    }
  }, [open]);

  const notValueType = (row: IModbusDeviceRow) => row.uiType !== UI_TYPE_VALUE;
  const isBinaryType = (row: IModbusDeviceRow) =>
    row.uiType === UI_TYPE_BINARY || row.uiType === UI_TYPE_REVERSE_BINARY;
  // A Coil or Discrete Input register is already a single bit — Bit # only
  // makes sense for a binary value packed into a Holding/Input register word.
  const needsBitNumber = (row: IModbusDeviceRow) => isBinaryType(row) && isBitPackableRegisterType(row.registerType);
  const updateRow = (rowIndex: number, mutator: (row: IModbusDeviceRow) => IModbusDeviceRow) =>
    setRows((prev) => {
      const next = [...prev];
      const before = next[rowIndex];
      let row = mutator(before);
      // Selecting a binary type seeds the value map with On/Off (value 1 = On,
      // value 0 = Off; reversed for reverse binary) so it works out of the box —
      // the user only opens the Map dialog to rename the labels.
      const becameBinary =
        (row.uiType === UI_TYPE_BINARY || row.uiType === UI_TYPE_REVERSE_BINARY) &&
        row.uiType !== before?.uiType;
      if (becameBinary && !row.customEnum?.["1"] && !row.customEnum?.["0"]) {
        const reversed = row.uiType === UI_TYPE_REVERSE_BINARY;
        row = { ...row, customEnum: { "1": reversed ? t`Off` : t`On`, "0": reversed ? t`On` : t`Off` } };
      }
      if (needsBitNumber(row) && row.bitNumber == null) {
        row = { ...row, bitNumber: 0 };
      } else if (!needsBitNumber(row) && row.bitNumber != null) {
        row = { ...row, bitNumber: null };
      }
      next[rowIndex] = row;
      return next;
    });

  const handleTest = async (index: number, row: IModbusDeviceRow) => {
    if (!deviceId || slaveId === "" || slaveId == null || row.registerAddress == null) {
      return;
    }
    setVerifyState((prev) => ({ ...prev, [index]: { status: "loading" } }));
    try {
      const res: any = await readRegister({
        device: deviceId,
        iface: iface ?? "",
        slave: Number(slaveId),
        // Align the test read with the device's configured timeout; fall back
        // to 2s when the field is empty/invalid (e.g. mid-edit).
        timeout: Number(timeoutMs) > 0 ? Number(timeoutMs) : 2000,
        registerType: row.registerType,
        registerAddress: row.registerAddress,
        dataType: row.dataType,
        multiplier: row.multiplier ?? 1,
        ...(row.bitNumber != null ? { bitNumber: row.bitNumber } : {}),
        ...(row.readFormula?.trim() ? { readFormula: row.readFormula.trim() } : {}),
        // TCP devices need connection params in the read-register payload.
        ...(host ? { host } : {}),
        ...(port !== "" && port != null ? { port: Number(port) } : {}),
      });
      // The SDK unwraps the backend's { success, data } envelope down to just
      // the raw reading — a failed read rejects (HTTP 400, caught below)
      // rather than resolving falsy, so only nullish means "no reading";
      // a legitimate reading of 0 must not be treated as a failure.
      if (res === undefined || res === null) {
        setVerifyState((prev) => ({ ...prev, [index]: { status: "failed" } }));
        return;
      }
      setVerifyState((prev) => ({ ...prev, [index]: { status: "success", value: res } }));
    } catch {
      setVerifyState((prev) => ({ ...prev, [index]: { status: "failed" } }));
    }
  };

  const rowsFromTemplate = (templateRows: IModbusFunctionalRow[] | undefined): IModbusDeviceRow[] =>
    (templateRows || []).map((r) => ({
      name: r.name,
      readOnly: r.readOnly,
      uiType: r.uiType,
      units: r.units,
      multiplier: r.multiplier ?? 1,
      minValue: r.minValue ?? null,
      maxValue: r.maxValue ?? null,
      step: r.step ?? null,
      customEnum: r.customEnum,
      errorPointEnabled: r.errorPointEnabled ?? false,
      errorPointValue: r.errorPointValue ?? null,
      // Carry the register mapping from the template; fall back to defaults for
      // older templates that were saved without register addressing.
      registerType: r.registerType ?? 2,
      registerAddress: r.registerAddress ?? null,
      dataType: r.dataType ?? "s16",
      readFormula: r.readFormula ?? undefined,
      writeFormula: r.writeFormula ?? undefined,
      bitNumber: r.bitNumber ?? null,
    }));

  const { mutate: validateFormula } = useValidateModbusFormula({
    onSuccess: (res: any) => {
      const idx = validatingFormulaIndexRef.current;
      if (idx == null || !res) {
        return;
      }
      validatedFormulaTextRef.current[idx] = res.readFormula ?? lastTrimmedFormulaRef.current;
      updateRow(idx, (r) => ({
        ...r,
        // Store the normalized string the backend parsed, not the raw text
        // typed, so what's saved and what's parsed can never drift apart.
        readFormula: res.readFormula ?? r.readFormula,
        // A 200 with invertible:false is still a real answer, not a partial
        // one — never keep a stale writeFormula from a previous edit.
        writeFormula: res.invertible ? res.writeFormula ?? null : null,
      }));
      setFormulaState((prev) => ({
        ...prev,
        [idx]: res.invertible
          ? { status: FORMULA_STATUS.IDLE }
          : {
            status: FORMULA_STATUS.WARN,
            message: res.reason || t`This formula can't be reversed — the register will read but not accept writes.`,
          },
      }));
    },
    onError: (err: any) => {
      const idx = validatingFormulaIndexRef.current;
      if (idx == null) {
        return;
      }
      validatedFormulaTextRef.current[idx] = lastTrimmedFormulaRef.current;
      setFormulaState((prev) => ({ ...prev, [idx]: { status: FORMULA_STATUS.ERROR, message: err?.message || t`Invalid formula` } }));
    },
  });

  const validateFormulaRow = async (
    index: number,
    row: IModbusDeviceRow,
  ): Promise<typeof FORMULA_STATUS.IDLE | typeof FORMULA_STATUS.WARN | typeof FORMULA_STATUS.ERROR> => {
    const trimmed = (row.readFormula ?? "").toString().trim();
    if (!trimmed) {
      updateRow(index, (r) => ({ ...r, readFormula: undefined, writeFormula: undefined }));
      setFormulaState((prev) => ({ ...prev, [index]: { status: FORMULA_STATUS.IDLE } }));
      validatedFormulaTextRef.current[index] = trimmed;
      return FORMULA_STATUS.IDLE;
    }
    validatingFormulaIndexRef.current = index;
    lastTrimmedFormulaRef.current = trimmed;
    setFormulaState((prev) => ({ ...prev, [index]: { status: FORMULA_STATUS.LOADING } }));
    const res: any = await validateFormula({ readFormula: trimmed });
    if (!res) {
      return FORMULA_STATUS.ERROR;
    }
    return res.invertible ? FORMULA_STATUS.IDLE : FORMULA_STATUS.WARN;
  };

  const reindexAfterRemove = <S,>(removedIndex: number) => (prev: Record<number, S>): Record<number, S> => {
    const next: Record<number, S> = {};
    Object.entries(prev).forEach(([key, value]) => {
      const idx = Number(key);
      if (idx === removedIndex) {
        return;
      }
      next[idx > removedIndex ? idx - 1 : idx] = value;
    });
    return next;
  };

  const handleRemoveRow = (index: number) => {
    setRows((r) => r.filter((_, idx) => idx !== index));
    setVerifyState(reindexAfterRemove(index));
    setFormulaState(reindexAfterRemove(index));
    validatedFormulaTextRef.current = Object.fromEntries(
      Object.entries(validatedFormulaTextRef.current)
        .filter(([key]) => Number(key) !== index)
        .map(([key, value]) => {
          const idx = Number(key);
          return [idx > index ? idx - 1 : idx, value];
        }),
    );
    
    const inFlight = validatingFormulaIndexRef.current;
    if (inFlight != null) {
      if (inFlight === index) {
        validatingFormulaIndexRef.current = null;
      } else if (inFlight > index) {
        validatingFormulaIndexRef.current = inFlight - 1;
      }
    }
  };

  const handleImportTemplate = (tpl: IModbusFunctionalTemplate) => {
    setRows(rowsFromTemplate(tpl.rows));
    if (!unitName.trim()) setUnitName(tpl.name);
    // Bring the template's control UI along (only when this unit has none yet,
    // so importing registers never silently overwrites an existing UI).
    if (tpl.defaultUiTemplate && !uiTemplate) {
      setUiTemplate(tpl.defaultUiTemplate as IUITemplate);
    }
    setImportLibraryOpen(false);
  };

  // A Control UI's points reference rows by index, so picking one from the
  // library must bring its own rows along — mixing it with the unit's
  // current rows would leave the mapping pointing at the wrong parameters.
  const hasEnteredParams = rows.some((r) => r.name?.trim());

  const handleSelectControlUi = async (selection: IControlUiSelection) => {
    if (hasEnteredParams) {
      const ok = await confirm({
        title: t`Override Parameters`,
        bodyMessage: t`Choosing a Control UI from the library will override the existing parameters. Do you want to continue?`,
        confirmText: t`Yes`,
        cancelText: t`No`,
      });
      if (!ok) return;
    }
    setRows(rowsFromTemplate(selection.rows));
    setUiTemplate(selection.defaultUiTemplate);
    setControlUiLibraryOpen(false);
  };

  const handleUiTypeChange = (
    index: number,
    currentUiType: IModbusDeviceRow["uiType"],
    nextUiType: IModbusDeviceRow["uiType"],
    update: (mutator: (row: IModbusDeviceRow) => IModbusDeviceRow) => void,
  ) => {
    const leavingValueType = currentUiType === UI_TYPE_VALUE && nextUiType !== UI_TYPE_VALUE;
    update((r) => ({
      ...r,
      uiType: nextUiType,
      ...(leavingValueType ? { readFormula: undefined, writeFormula: undefined } : {}),
    }));
    if (!leavingValueType) {
      return;
    }
    setFormulaState((prev) => {
      if (!(index in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[index];
      return next;
    });
    delete validatedFormulaTextRef.current[index];
    if (validatingFormulaIndexRef.current === index) {
      validatingFormulaIndexRef.current = null;
    }
  };

  const columns: IRegisterColumn<IModbusDeviceRow>[] = [
    { key: "name", label: t`Parameter Name`, width: 160, kind: "text", sticky: true },
    {
      key: "uiType",
      label: t`Type`,
      tooltip: t`The type of parameter that is stored in this register`,
      width: 120,
      render: ({ row, index, update }) => (
        <Select
          fullWidth
          menuPortal
          classes={{ trigger: registerTableStyles.selectTrigger }}
          options={UI_TYPE_OPTIONS}
          value={row.uiType}
          onChange={(v: string | number) =>
            handleUiTypeChange(index, row.uiType, v as IModbusDeviceRow["uiType"], update)
          }
        />
      ),
    },
    { key: "units", label: t`Eng Units`, tooltip: t`Unit of measurement for this parameter's value (e.g. kW, °C)`, width: 90, kind: "select", options: ENG_UNIT_OPTIONS, maxMenuHeight: 260, minMenuWidth: 120 },
    { key: "minValue", label: t`Min`, tooltip: t`Lowest valid value this parameter can report or accept`, width: 80, render: ({ row, update }) => notValueType(row) ? <span style={{ color: "#aaa" }}>—</span> : <TextField size="small" type="number" fullWidth value={String(row.minValue ?? "")} onChange={(v: string) => update(r => ({ ...r, minValue: v === "" ? null : Number(v) }))} /> },
    { key: "maxValue", label: t`Max`, tooltip: t`Highest valid value this parameter can report or accept`, width: 80, render: ({ row, update }) => notValueType(row) ? <span style={{ color: "#aaa" }}>—</span> : <TextField size="small" type="number" fullWidth value={String(row.maxValue ?? "")} onChange={(v: string) => update(r => ({ ...r, maxValue: v === "" ? null : Number(v) }))} /> },
    { key: "step", label: t`Step`, tooltip: t`The resolution of change between two adjacent values`, width: 80, render: ({ row, update }) => notValueType(row) ? <span style={{ color: "#aaa" }}>—</span> : <TextField size="small" type="number" fullWidth placeholder="1" value={String(row.step ?? "")} onChange={(v: string) => update(r => ({ ...r, step: v === "" ? null : Number(v) }))} /> },
    { key: "readOnly", label: t`R/W`, tooltip: t`Register is read-only, write-only, or read/write`, width: 80, kind: "rwBool" },
    { key: "multiplier", label: t`Scale`, tooltip: t`Divider applied to the raw register value`, width: 90, render: ({ row, update }) => notValueType(row) ? <span style={{ color: "#aaa" }}>—</span> : <TextField size="small" type="number" fullWidth value={String(row.multiplier ?? "")} onChange={(v: string) => update(r => ({ ...r, multiplier: v === "" ? 1 : Number(v) }))} /> },
    { key: "registerType", label: t`Reg Type`, tooltip: t`Modbus register category, sets the address range and access rules.`, width: 140, kind: "select", options: REG_TYPE_NUMERIC_OPTIONS },
    { key: "registerAddress", label: t`Register`, tooltip: t`Modbus register address of this parameter.`, width: 90, kind: "number", min: 1, max: 65536, integer: true },
    {
      key: "bitNumber",
      label: t`Bit #`,
      tooltip: t`Bit position (0-15) in the holding register that holds the discrete value`,
      width: 80,
      render: ({ row, update }) =>
        !supportsBitConvert
          ? <Tooltip title={t`Unavailable on current firmware. Update to v1.11 or later to use this feature.`} placement="top"><span style={{ color: "#aaa" }}>—</span></Tooltip>
          : needsBitNumber(row) ? (
              <Select
                fullWidth
                menuPortal
                maxMenuHeight={260}
                classes={{ trigger: registerTableStyles.selectTrigger }}
                options={BIT_NUMBER_OPTIONS}
                value={row.bitNumber ?? 0}
                onChange={(v: string | number) => update((r) => ({ ...r, bitNumber: Number(v) }))}
              />
            ) : (
              <span style={{ color: "#aaa" }}>—</span>
            ),
    },
    { key: "dataType", label: t`Data Type`, tooltip: t`Raw bytes interpretation, sets range and precision.`, width: 120, kind: "select", options: DATA_TYPE_CODE_OPTIONS },
    {
      key: "customEnum",
      label: t`Value Map`,
      tooltip: t`Table that maps Modbus values to textual application names (e.g., 0 = Off, 1 = On, 2 = Auto)`,
      width: 100,
      render: ({ row, index }) => {
        if (isBinaryType(row)) {
          const cnt = [row.customEnum?.["1"], row.customEnum?.["0"]].filter(Boolean).length;
          return (
            <Button white size="small" classes={{ root: styles.mapBtn }} onClick={() => setBinaryTarget(index)}>
              {cnt > 0 ? `${t`Map`} (${cnt})` : t`Map`}
            </Button>
          );
        }
        if (row.uiType === UI_TYPE_ENUMERATION) {
          const cnt = row.customEnum ? Object.keys(row.customEnum).length : 0;
          return (
            <Button white size="small" classes={{ root: styles.mapBtn }} onClick={() => setEnumTarget(index)}>
              {cnt > 0 ? `${t`Map`} (${cnt})` : t`Map`}
            </Button>
          );
        }
        return null;
      },
    },
    {
      key: "readFormula",
      label: t`Convert`,
      tooltip: (
        <>
          {t`(Optional) Set a logical formula based on the Input value (V), numbers and basic algebric signs`}{" "}
          <b>+, -, *, /, (, )</b>. {t`Example:`} <b>V*2/10</b>
        </>
      ),
      width: 200,
      render: ({ row, index, update }) => {
        if (!supportsBitConvert) {
          return <Tooltip title={t`Unavailable on current firmware. Update to v1.11 or later to use this feature.`} placement="top"><span style={{ color: "#aaa" }}>—</span></Tooltip>;
        }
        if (notValueType(row)) {
          return <span style={{ color: "#aaa" }}>—</span>;
        }
        const fs = formulaState[index] ?? { status: FORMULA_STATUS.IDLE };
        return (
          <div className={styles.formulaCell}>
            <Row gap={6} align="center">
              <TextField
                size="small"
                fullWidth
                error={fs.status === FORMULA_STATUS.WARN || fs.status === FORMULA_STATUS.ERROR}
                value={row.readFormula ?? ""}
                onChange={(v: string) => update((r) => ({ ...r, readFormula: v, writeFormula: undefined }))}
                onBlur={() => {
                  if (skipFormulaBlurRef.current) {
                    skipFormulaBlurRef.current = false;
                    return;
                  }
                  void validateFormulaRow(index, row);
                }}
              />
              {fs.status === FORMULA_STATUS.LOADING ?  <Spinner size={12} /> : <div className={styles.emptyDiv}></div>}
            </Row>
            {fs.status === FORMULA_STATUS.WARN && <span className={`${styles.formulaMessage} ${styles.formulaWarn}`}>{fs.message}</span>}
            {fs.status === FORMULA_STATUS.ERROR && <span className={`${styles.formulaMessage} ${styles.testError}`}>{fs.message}</span>}
          </div>
        );
      },
    },
    {
      label: t`Verify`,
      tooltip: t`Click the test button to read the register current value`,
      width: 180,
      render: ({ row, index }: { row: IModbusDeviceRow; index: number }) => {
        const vs = verifyState[index] ?? { status: "idle" };
        const canTest = !!deviceId && slaveId !== "" && slaveId != null && row.registerAddress != null;
        return (
          <div className={styles.testCluster}>
            <Button
              white
              size="small"
              disabled={vs.status === "loading" || !canTest}
              onClick={() => handleTest(index, row)}
            >
              {vs.status === "loading" ? <Spinner size={14} /> : t`Test`}
            </Button>
            {vs.status === "idle" && <span className={styles.testIdle}>—</span>}
            {vs.status === "loading" && <span className={styles.testLoading}>Fetching...</span>}
            {vs.status === "success" && (
              <span className={styles.testSuccess}>
                {vs.value} {row.units}
              </span>
            )}
            {vs.status === "failed" && (
              <span className={styles.testError}>{t`Failed`}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "errorPointEnabled",
      label: t`Error Point`,
      tooltip: t`Map this register to an error. Once checked, set the register value that clears the error.`,
      width: 160,
      render: ({ row, update }) => (
        <ErrorPointCell
          enabled={row.errorPointEnabled}
          clearValue={row.errorPointValue}
          onToggle={() =>
            update((r) =>
              r.errorPointEnabled
                ? { ...r, errorPointEnabled: false, errorPointValue: null }
                : { ...r, errorPointEnabled: true },
            )
          }
          onChangeClearValue={(v) => update((r) => ({ ...r, errorPointValue: v }))}
        />
      ),
    },
    ...(entityType ? [{
      key: "property" as keyof IModbusDeviceRow,
      label: t`Universal Point`,
      tooltip: t`Check and map this parameter to a specific universal point (e.g. Setpoint for an AC unit, or Energy for a power meter).`,
      width: 200,
      render: ({ row, index }: { row: IModbusDeviceRow; index: number }) => {
        const propDef = row.property ? propsForType[row.property] : null;
        // A universal point may be mapped only once: disable points already
        // taken by other rows so they can't be picked again here.
        const usedByOthers = new Set(
          rows.filter((_, i) => i !== index).map((r) => r.property).filter(Boolean) as string[],
        );
        const rowOptions = [
          { value: "", label: t`None` },
          ...universalOptions.map((o) => ({
            ...o,
            disabled: usedByOthers.has(o.value),
          })),
        ];
        return (
          <Row gap={6} align="center">
            <UniversalCell
              enabled={row.property !== undefined}
              point={row.property}
              options={rowOptions}
              onToggle={() => {
                const enabling = row.property === undefined;
                updateRow(index, (r) =>
                  r.property !== undefined
                    ? { ...r, property: undefined, propertyMapping: undefined }
                    : { ...r, property: "" },
                );
                if (enabling) {
                  scrollTableRight();
                }
              }}
              onChangePoint={(v) => {
                updateRow(index, (r) => ({ ...r, property: v, propertyMapping: undefined }));
                scrollTableRight();
              }}
            />
            {row.property && propDef?.enum && (
              <Button white size="small" classes={{ root: styles.mapBtn }} onClick={() => setPropertyTarget(index)}>
                {t`Map`}
              </Button>
            )}
          </Row>
        );
      },
    }] as IRegisterColumn<IModbusDeviceRow>[] : []),
  ];

  // A power meter unit is useless without at least its Energy point mapped.
  const energyMissing = entityType === "powerMeter" && !rows.some((r) => r.property === "energy");
  const hasFormulaError = rows.some((row, index) => !notValueType(row) && formulaState[index]?.status === FORMULA_STATUS.ERROR);
  const hasFormulaWarning = rows.some((row, index) => !notValueType(row) && formulaState[index]?.status === FORMULA_STATUS.WARN);
  const isFormulaLoading = rows.some((row, index) => !notValueType(row) && formulaState[index]?.status === FORMULA_STATUS.LOADING);
  const hasDirtyFormulas = rows.some((row, index) => {
    if (notValueType(row)) {
      return false;
    }
    const trimmed = (row.readFormula ?? "").toString().trim();
    return (validatedFormulaTextRef.current[index] ?? "") !== trimmed;
  });
  const hasBlockingFormulaIssue = hasFormulaError || hasFormulaWarning || isFormulaLoading || hasDirtyFormulas;

  const handleSave = async () => {
    setSubmitted(true);
    if (!unitName.trim()) return;
    if (energyMissing) return;

    const dirtyRows = rows
      .map((row, index) => ({ row, index, trimmed: (row.readFormula ?? "").toString().trim() }))
      .filter(({ row, index, trimmed }) => !notValueType(row) && (validatedFormulaTextRef.current[index] ?? "") !== trimmed);
    const dirtyIndexes = new Set(dirtyRows.map(({ index }) => index));

    let freshlyBlocked = false;
    if (dirtyRows.length > 0) {
      setIsValidatingFormulas(true);
      try {
        for (const { row, index } of dirtyRows) {
          const status = await validateFormulaRow(index, row);
          if (status === FORMULA_STATUS.WARN || status === FORMULA_STATUS.ERROR) {
            freshlyBlocked = true;
          }
        }
      } finally {
        setIsValidatingFormulas(false);
      }
    }
    const blockedElsewhere = Object.entries(formulaState).some(([key, s]) => {
      if (dirtyIndexes.has(Number(key))) {
        return false;
      }
      return s.status === FORMULA_STATUS.WARN || s.status === FORMULA_STATUS.ERROR;
    });
    if (freshlyBlocked || blockedElsewhere) {
      return;
    }

    onSave(unitName.trim(), rows, !!entityType, uiTemplate, entityType || undefined);
  };

  const nameError = submitted && !unitName.trim() ? t`Required` : undefined;
  const energyError = submitted && energyMissing
    ? t`Map a register to Energy before saving a power meter unit.`
    : undefined;
  const formulaError = submitted && hasBlockingFormulaIssue
    ? t`Fix the Convert formula errors or warnings before saving.`
    : undefined;

  const parsedTemplateName = uiTemplate?.name ?? null;

  const ptRow = propertyTarget != null ? rows[propertyTarget] : null;
  const ptProp = ptRow?.property ? propsForType[ptRow.property] : null;
  const ptUniversalEntries = ptProp?.enum
    ? Object.entries(ptProp.enum).map(([key, def]: [string, any]) => ({
      value: String(def.value ?? key),
      label: def.text || key,
    }))
    : [];

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        onMouseDown={handleDialogMouseDown}
        title={initialRows && initialRows.length > 0 ? t`Edit Functional Unit` : t`Add Functional Unit`}
        onConfirm={handleSave}
        confirmLabel={t`Save Unit`}
        cancelLabel={t`Cancel`}
        loading={saving || isValidatingFormulas}
        confirmDisabled={hasBlockingFormulaIssue}
        size="full"
        staticBackdrop
        footerLeftContent={
          energyError ? (
            <Typography variant="meta" style={{ color: "#d95353" }}>{energyError}</Typography>
          ) : undefined
        }
      >
        <div className={styles.unitCard}>

          {/* Gray card header */}
          <div className={styles.unitCardHead}>
            <div className={styles.unitCardHeadLeft}>
              {unitIndex != null && (
                <div className={styles.badge}>{unitIndex + 1}</div>
              )}
              <div className={styles.nameFieldWrap}>
                <TextField
                  label={t`App Unit Name`}
                  fullWidth
                  value={unitName}
                  onChange={(v: string) => setUnitName(v)}
                  error={nameError}
                  placeholder={t`e.g., Compressor Control`}
                />
              </div>
              <div className={styles.uiTemplateGroup}>
                <div className={styles.uiTemplateWrap}>
                  <label className={styles.uiTemplateFieldLabel}>{t`Control UI`}</label>
                  <div className={styles.uiTemplateField}>
                    <span className={`${styles.uiTemplateLabel} ${parsedTemplateName ? styles.uiTemplateLabelSet : ""}`}>
                      {parsedTemplateName ?? t`Not set up yet`}
                    </span>
                    <button className={styles.uiTemplateBtn} onClick={() => setUiTemplateOpen(true)}>
                      {parsedTemplateName ? t`Edit` : `+ ${t`Create`}`}
                    </button>
                  </div>
                </div>
                {parsedTemplateName && (
                  <IconButton
                    disableRipple
                    title={t`Remove Control UI`}
                    style={{ marginTop: 20 }}
                    onClick={async () => {
                      const ok = await confirm({
                        title: t`Remove Control UI`,
                        bodyMessage: t`Are you sure you want to remove this Control UI? The unit's registers are not affected.`,
                        confirmText: t`Remove`,
                        cancelText: t`Cancel`,
                      });
                      if (ok) setUiTemplate(undefined);
                    }}
                  >
                    <DeleteIcon color="#aaa2aa" />
                  </IconButton>
                )}
              </div>
              <Row gap={12} align="center" style={{ paddingTop: '20px' }}>
                <label className={styles.checkboxLabel}>
                  <Checkbox
                    size="small"
                    checked={entityType !== ""}
                    onChange={() => {
                      if (entityType) {
                        // Unchecking clears the mapping entirely — old rows'
                        // points don't mean anything once nothing is selected.
                        setRows((prev) => prev.map((r) => ({ ...r, property: undefined, propertyMapping: undefined })));
                        setEntityType("");
                      } else {
                        setEntityType("controlUnit"); // default
                        scrollTableRight();
                      }
                    }}
                  />
                  <Typography component="span" variant="body">{t`Map to Universal`}</Typography>
                </label>
                {entityType && (
                  <Select
                    size="medium"
                    menuPortal
                    style={{ minWidth: 110 }}
                    options={MODBUS_ENTITY_TYPE_OPTIONS}
                    value={entityType}
                    onChange={(v: string | number) => {
                      const next = v as ModbusEntityType;
                      if (next !== entityType) {
                        // Different universal entity — the old type's points don't apply.
                        setRows((prev) => prev.map((r) => ({ ...r, property: undefined, propertyMapping: undefined })));
                      }
                      setEntityType(next);
                    }}
                  />
                )}
              </Row>
            </div>
            <Row gap={8}>
              <Button white size="small" onClick={() => setControlUiLibraryOpen(true)}>{t`Control UI Library`}</Button>
              <Button white size="small" onClick={() => setImportLibraryOpen(true)}>{t`Functional Unit Library`}</Button>
            </Row>
          </div>

          {/* Table area */}
          <div className={styles.tableArea}>
            {rows.length === 0 ? (
              <Typography variant="meta" className={styles.emptyState}>
                {t`Click "+ Add Register" to add register parameters.`}
              </Typography>
            ) : (
              <RegisterTable<IModbusDeviceRow>
                columns={columns}
                rows={rows}
                onUpdateRow={updateRow}
                onRemoveRow={handleRemoveRow}
                scrollX
                scrollRef={tableScrollRef}
                className={styles.topAlignTable}
              />
            )}
          </div>

          {/* Add Register */}
          <div className={styles.addRegisterWrap}>
            <Button white size="small" onClick={() => setRows((r) => [...r, newRow()])}>
              + {t`Add Register`}
            </Button>
          </div>

        </div>
      </BaseDialog>

      <EnumMapEditor
        open={enumTarget !== null}
        label={enumTarget != null ? rows[enumTarget]?.name || t`Parameter` : ""}
        initialEntries={enumTarget != null ? recordToEntries(rows[enumTarget]?.customEnum) : []}
        onClose={() => setEnumTarget(null)}
        onSave={(entries) => {
          if (enumTarget == null) return;
          const map = Object.fromEntries(entries.map((e) => [e.value, e.text]));
          updateRow(enumTarget, (r) => ({ ...r, customEnum: map }));
          setEnumTarget(null);
        }}
      />

      <BinaryMapDialog
        open={binaryTarget !== null}
        paramName={binaryTarget != null ? rows[binaryTarget]?.name : undefined}
        isReverseBinary={binaryTarget != null ? rows[binaryTarget]?.uiType === UI_TYPE_REVERSE_BINARY : undefined}
        value={binaryTarget != null ? rows[binaryTarget]?.customEnum : undefined}
        onClose={() => setBinaryTarget(null)}
        onSave={(map: ModbusCustomEnum) => {
          if (binaryTarget == null) return;
          updateRow(binaryTarget, (r) => ({ ...r, customEnum: map }));
          setBinaryTarget(null);
        }}
      />

      <PropertyMappingDialog
        open={propertyTarget !== null}
        paramName={ptRow?.name}
        propertyName={ptProp?.text}
        universalEntries={ptUniversalEntries}
        registerEnum={ptRow?.customEnum || {}}
        value={ptRow?.propertyMapping as Record<string, string> | undefined}
        onClose={() => setPropertyTarget(null)}
        onSave={(mapping) => {
          if (propertyTarget == null) return;
          updateRow(propertyTarget, (r) => ({ ...r, propertyMapping: mapping }));
          setPropertyTarget(null);
        }}
      />

      <ImportFromLibraryDialog
        open={importLibraryOpen}
        siteId={siteId}
        loadedTemplateIds={new Set()}
        onClose={() => setImportLibraryOpen(false)}
        onLoad={handleImportTemplate}
      />

      <ControlUiLibraryDialog
        open={controlUiLibraryOpen}
        siteId={siteId}
        onClose={() => setControlUiLibraryOpen(false)}
        onSelect={handleSelectControlUi}
      />

      <UITemplateMapperDialog
        open={uiTemplateOpen}
        rows={rows}
        initialTemplate={uiTemplate}
        existingNames={existingControlUiNames}
        onClose={() => setUiTemplateOpen(false)}
        onSave={(tpl) => setUiTemplate(tpl)}
      />
    </>
  );
};

export default AddFunctionalUnitModal;
